import { Component } from 'preact';
import { toast } from 'sonner';
import { Settings, Trophy } from 'lucide-react';
import { GOLD, ACCENT_GOLD, TEXT_PRIMARY, TEXT_MUTED } from '../../lib/medievalTheme';
import { CoinIcon, ShardIcon } from '../ui/icons';
import { resolveAvatarUrl } from '../../utils/arena/avatarUtils';
import AvatarImage from '../ui/AvatarImage';
import { playUI, UI } from '../../utils/arena/uiSounds';
import AddToHomePrompt from './AddToHomePrompt';
import MobileLeaderboard from './MobileLeaderboard';
import MobileSettings from './MobileSettings';
import MobileProfile from './MobileProfile';
import { k, t } from '../../localization/t';

// Phone hub. Hearthstone-style landscape layout: full-screen background
// art, corner HUD chips, a centered stack of big primary plates, and
// banner actions in the bottom corners. Sized in device rems (never cqw)
// with >=44px tap targets and active-press feedback; see the phone-mode
// notes in index.css. This is a DEDICATED mobile screen; it shares the
// app's state and handlers with ArenaHub but none of its markup.

// Destinations whose screens have no mobile layout yet surface a toast
// instead of navigating into a desktop-sized screen with no way back.
// Remove entries as each mobile screen lands.
const NOT_ON_MOBILE_YET = k('Coming to mobile soon. This screen is next in line.');

const PLATE_BASE = {
  border: `1px solid ${GOLD} 0.4)`,
  borderRadius: 'var(--radius-lg)',
  background: 'linear-gradient(180deg, rgba(38,30,18,0.92) 0%, rgba(22,16,9,0.94) 55%, rgba(14,10,6,0.96) 100%)',
  boxShadow: 'inset 0 1px 0 rgba(255,230,170,0.10), inset 0 -2px 6px rgba(0,0,0,0.55), 0 4px 14px rgba(0,0,0,0.5)',
  color: TEXT_PRIMARY,
  textShadow: '0 1px 2px rgba(0,0,0,0.8)',
};

const CHIP_BASE = {
  background: 'linear-gradient(180deg, rgba(20,16,10,0.85) 0%, rgba(10,8,5,0.9) 100%)',
  border: `1px solid ${GOLD} 0.28)`,
  borderRadius: 'var(--radius-full)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
};

function PlateButton({ label, primary = false, onClick }) {
  return (
    <button
      type="button"
      className="arena-heading font-bold w-full transition-transform duration-100 active:translate-y-px active:brightness-110"
      style={{
        ...PLATE_BASE,
        minHeight: primary ? '3.25rem' : '2.75rem',
        fontSize: primary ? '1.25rem' : '1rem',
        letterSpacing: '0.04em',
        ...(primary ? { border: `1px solid ${GOLD} 0.65)`, boxShadow: `${PLATE_BASE.boxShadow}, 0 0 18px rgba(212,168,67,0.18)` } : {}),
      }}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

// Hearthstone-style corner banners: absolutely positioned so they BLEED
// past the canvas' safe-area padding into the physical screen corners
// (absolute offsets resolve against the canvas' padding box, i.e. the
// glass edge). Sitting 0.5rem off the corner, the corner facing the
// glass takes a concentric radius: screen radius minus that 0.5rem gap,
// floored at the base radius on square-screened devices.
const CORNER_GLASS_RADIUS = 'max(0.5rem, calc(var(--screen-radius, 0px) - 0.5rem))';
const CORNER_POS = {
  bl: { left: '0.5rem', bottom: '0.5rem', borderBottomLeftRadius: CORNER_GLASS_RADIUS, paddingLeft: '1.5rem' },
  br: { right: '0.5rem', bottom: '0.5rem', borderBottomRightRadius: CORNER_GLASS_RADIUS, paddingRight: '1.5rem' },
};

function CornerButton({ label, corner, onClick }) {
  return (
    <button
      type="button"
      className="absolute z-10 arena-heading font-semibold transition-transform duration-100 active:translate-y-px active:brightness-110"
      style={{
        ...PLATE_BASE,
        minHeight: '2.75rem',
        padding: '0 1.25rem',
        fontSize: '0.9375rem',
        letterSpacing: '0.03em',
        ...CORNER_POS[corner],
      }}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

export default class MobileHub extends Component {
  state = { showLeaderboard: false, showSettings: false, profileId: null };

  guard = (handler) => () => {
    playUI(UI.CLICK);
    if (handler) handler();
    else toast(t(NOT_ON_MOBILE_YET));
  };

  render() {
    const { profile, sorceryCards, version } = this.props;
    const avatarUrl = resolveAvatarUrl(profile, sorceryCards);

    return (
      <div className="absolute inset-0 flex flex-col overflow-hidden select-none">
        {/* Background art + vignette. Same hub art as desktop. */}
        <img
          src="/hub-bg.webp"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.15) 30%, rgba(0,0,0,0.15) 65%, rgba(0,0,0,0.6) 100%)' }}
        />

        {/* ── Top HUD row. The hub root spans the full glass (absolute
            inset-0 escapes the canvas' safe-area padding, which is what
            lets the corner banners bleed), so this row carries its own
            notch-aware side padding. ── */}
        <div
          className="relative z-10 flex items-center justify-between pt-2"
          style={{
            paddingLeft: 'max(0.75rem, env(safe-area-inset-left, 0px))',
            paddingRight: 'max(0.75rem, env(safe-area-inset-right, 0px))',
          }}
        >
          {/* Profile chip */}
          <button
            type="button"
            className="flex items-center gap-2 pr-4 transition-transform duration-100 active:scale-[0.97]"
            style={{ ...CHIP_BASE, minHeight: '2.75rem', paddingLeft: '0.25rem' }}
            onClick={() => { playUI(UI.CLICK); this.setState({ profileId: profile.id }); }}
          >
            {avatarUrl ? (
              <AvatarImage
                src={avatarUrl}
                className="rounded-full"
                style={{ width: '2.25rem', height: '2.25rem', border: `1px solid ${GOLD} 0.4)` }}
              />
            ) : (
              <div
                className="rounded-full flex items-center justify-center font-bold"
                style={{ width: '2.25rem', height: '2.25rem', background: `${GOLD} 0.12)`, border: `1px solid ${GOLD} 0.4)`, color: TEXT_PRIMARY }}
              >
                {(profile?.name || '?')[0].toUpperCase()}
              </div>
            )}
            <div className="flex flex-col items-start leading-tight">
              <span className="text-sm font-bold" style={{ color: TEXT_PRIMARY, textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>
                {profile?.name || t('Adventurer')}
              </span>
              {profile?.rank?.tier ? (
                <span className="text-xs" style={{ color: ACCENT_GOLD }}>
                  {profile.rank.tier}{profile.rank.division ? ` ${profile.rank.division}` : ''}
                </span>
              ) : null}
            </div>
          </button>

          {/* Currencies + settings */}
          <div className="flex items-center gap-2">
            {profile?.coins != null ? (
              <div className="flex items-center gap-1.5 px-3" style={{ ...CHIP_BASE, minHeight: '2.25rem' }}>
                <CoinIcon size={14} />
                <span className="text-sm font-bold tabular-nums" style={{ color: '#f0d060' }}>{profile.coins}</span>
              </div>
            ) : null}
            {profile?.arcanaShards != null ? (
              <div className="flex items-center gap-1.5 px-3" style={{ ...CHIP_BASE, minHeight: '2.25rem' }}>
                <ShardIcon size={13} />
                <span className="text-sm font-bold tabular-nums" style={{ color: '#7dd3fc' }}>{profile.arcanaShards}</span>
              </div>
            ) : null}
            <button
              type="button"
              aria-label={t('Leaderboard')}
              className="flex items-center justify-center transition-transform duration-100 active:scale-[0.94]"
              style={{ ...CHIP_BASE, width: '2.75rem', height: '2.75rem' }}
              onClick={() => { playUI(UI.CLICK); this.setState({ showLeaderboard: true }); }}
            >
              <Trophy size={19} color={ACCENT_GOLD} />
            </button>
            <button
              type="button"
              aria-label={t('Settings')}
              className="flex items-center justify-center transition-transform duration-100 active:scale-[0.94]"
              style={{ ...CHIP_BASE, width: '2.75rem', height: '2.75rem' }}
              onClick={() => { playUI(UI.CLICK); this.setState({ showSettings: true }); }}
            >
              <Settings size={20} color={TEXT_MUTED} />
            </button>
          </div>
        </div>

        {/* ── Center plates ── */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center gap-2.5 px-6">
          <div className="w-full flex flex-col gap-2.5" style={{ maxWidth: '20rem' }}>
            <PlateButton primary label={t('Play')} onClick={this.guard(this.props.onOpenPlay)} />
            <PlateButton label={t('Deck Builder')} onClick={this.guard(this.props.onOpenDeckBuilder)} />
            <PlateButton label={t('Arcane Trials')} onClick={this.guard(this.props.onOpenArcaneTrials)} />
          </div>
        </div>

        <AddToHomePrompt />

        {this.state.showLeaderboard ? (
          <MobileLeaderboard
            profile={profile}
            sorceryCards={sorceryCards}
            onViewProfile={(id) => this.setState({ profileId: id })}
            onClose={() => this.setState({ showLeaderboard: false })}
          />
        ) : null}

        {this.state.showSettings ? (
          <MobileSettings
            profile={profile}
            version={this.props.version}
            onLogout={this.props.onLogout}
            onClose={() => this.setState({ showSettings: false })}
          />
        ) : null}

        {/* Rendered last so it stacks above whichever overlay opened it. */}
        {this.state.profileId ? (
          <MobileProfile
            playerId={this.state.profileId}
            selfProfile={profile}
            sorceryCards={sorceryCards}
            onClose={() => this.setState({ profileId: null })}
          />
        ) : null}

        {/* ── Bottom corner banners (absolute, bleed to the glass) ── */}
        <CornerButton label={t('Store')} corner="bl" onClick={this.guard(this.props.onOpenStore)} />
        <CornerButton label={t('Auction House')} corner="br" onClick={this.guard(this.props.onOpenAuctionHouse)} />
        {version ? (
          <span
            className="absolute left-1/2 -translate-x-1/2 bottom-2 z-10 text-xs tabular-nums"
            style={{ color: TEXT_MUTED, textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}
          >
            v{version}
          </span>
        ) : null}
      </div>
    );
  }
}
